import React from "react";

export default function Mint() {
  /* Mapping the BlackBook/Mosaic theme colors to Tailwind classes:
    --color-bg: #000000 (We use a very dark gray equivalent for better contrast in Tailwind)
    --color-section-bg: #0b0f0d (Darkest sections)
    --color-card-bg: #151515 (Card/Box background)
    --color-accent: #3498DB (Deep Blue Accent)
    --color-accent-hover: #1A5276 (Darker Blue)
  */
  
  const ACCENT_BLUE = '#3498DB';
  const ACCENT_BLUE_HOVER = '#1A5276';
  const DARK_BG = '#0b0f0d'; // Corresponds to --color-section-bg
  const CARD_BG = '#151515'; // Corresponds to --color-card-bg
  const LIGHT_CARD_BG = '#1f1f1f'; // Slightly lighter card bg for layers
  
  return (
    <div 
      style={{ backgroundColor: DARK_BG }} 
      className="text-white min-h-screen px-6 py-20 flex flex-col items-center text-center"
    >
      {/* Hero Section */}
      <div className="max-w-4xl mb-16">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Mosaic Global Tech
        </h1>
        <h2 
          style={{ color: ACCENT_BLUE }} 
          className="text-2xl md:text-3xl mb-6"
        >
          NFT & Blockchain Solutions
        </h2>
        <p className="text-lg md:text-xl mb-8 text-gray-300">
          Bridging traditional development and Web3 innovation with super fast
          delivery
        </p>

        {/* Placeholder for hero image - Theme adjusted */}
        <div 
          style={{ backgroundColor: CARD_BG }} 
          className="h-64 rounded-xl flex items-center justify-center mb-10 border border-gray-700 shadow-xl"
        >
          <p className="text-gray-500">
            [Hero banner/illustration placeholder]
          </p>
        </div>
      </div>

      {/* About Section */}
      <div 
        style={{ backgroundColor: CARD_BG }} 
        className="rounded-2xl p-8 w-full max-w-4xl shadow-lg mb-16 text-left border border-gray-800"
      >
        <h2 className="text-2xl font-semibold mb-6 border-b border-gray-700 pb-4">
         About Our Platform
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 
              style={{ color: ACCENT_BLUE }} 
              className="text-xl font-bold mb-4"
            >
              Web3 Development Boom
            </h3>
            <p className="mb-4 text-gray-300">
              Smart-contract deployments jumped 302% quarter-over-quarter in Q2
              2023, yet only 7% of game studios actively use NFT tools.
            </p>
            <p className="text-gray-300">
              We make blockchain integration accessible for all developers and
              creators.
            </p>
          </div>
          <div>     
            <h3 
              style={{ color: ACCENT_BLUE }} 
              className="text-xl font-bold mb-4"
            >
              Mosaic Global Mint
            </h3>
            <p className="mb-4 text-gray-300">
              Our NFT platform simplifies minting with lazy minting and
              multi-chain support (from $0.05 on Solana to Ethereum/Polygon).
            </p>
            <p className="text-gray-300">
              No blockchain expertise needed - we handle all smart contract
              logic.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div 
        style={{ backgroundColor: CARD_BG }} 
        className="rounded-2xl p-8 w-full max-w-4xl shadow-lg mb-16 border border-gray-800"
      >
        <h2 className="text-2xl font-semibold mb-6">💎 Pricing & Packages</h2>

        <div className="grid gap-6 md:grid-cols-3 text-left mb-10">
          {/* Starter Plan */}
          <div 
            style={{ backgroundColor: LIGHT_CARD_BG }} 
            className="rounded-xl p-5 border border-gray-700"
          >
            <h3 className="text-xl font-bold mb-2">✨ Starter Plan</h3>
            <p style={{ color: ACCENT_BLUE }} className="mb-3">Ideal for individual creators</p>
            <ul className="list-disc list-inside text-sm text-gray-400 space-y-2">
              <li>Responsive website + 10 NFT drop</li>
              <li>Design template included</li>
              <li>Single-chain integration</li>
              <li>Basic support</li>
            </ul>
          </div>

          {/* Professional Plan (Highlighted) */}
          <div 
            style={{ backgroundColor: LIGHT_CARD_BG, borderColor: ACCENT_BLUE }} 
            className="rounded-xl p-5 border-2 shadow-xl"
          >
            <h3 className="text-xl font-bold mb-2">🚀 Professional Plan</h3>
            <p style={{ color: ACCENT_BLUE }} className="mb-3">For growing projects</p>
            <ul className="list-disc list-inside text-sm text-gray-400 space-y-2">
              <li>Custom website/web app</li>
              <li>Up to 50 NFT collection</li>
              <li>Multi-chain deployment</li>
              <li>Marketing assets included</li>
            </ul>
          </div>

          {/* Enterprise Plan */}
          <div 
            style={{ backgroundColor: LIGHT_CARD_BG }} 
            className="rounded-xl p-5 border border-gray-700"
          >
            <h3 className="text-xl font-bold mb-2">👑 Enterprise Plan</h3>
            <p style={{ color: ACCENT_BLUE }} className="mb-3">End-to-end Web3 solutions</p>
            <ul className="list-disc list-inside text-sm text-gray-400 space-y-2">
              <li>Custom marketplace development</li>
              <li>Unlimited NFT collection size</li>
              <li>Token-gated features</li>
              <li>Dedicated project management</li>
            </ul>
          </div>
        </div>
        <p style={{ color: ACCENT_BLUE }} className="text-sm">
          * Early clients qualify for discounted services
        </p>
      </div>

      {/* Portfolio Section */}
      <div 
        style={{ backgroundColor: CARD_BG }} 
        className="rounded-2xl p-8 w-full max-w-4xl shadow-lg mb-16 border border-gray-800"
      >
        <h2 className="text-2xl font-semibold mb-6">🎨 Our Portfolio</h2>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Project 1 */}
          <div 
            style={{ backgroundColor: LIGHT_CARD_BG }} 
            className="rounded-xl p-5 text-left border border-gray-700"
          >
            <div 
              style={{ backgroundColor: CARD_BG }} 
              className="h-40 rounded-lg mb-4 flex items-center justify-center"
            >
              <p className="text-gray-500">[NFT Marketplace screenshot]</p>
            </div>
            <h3 className="text-lg font-bold mb-2">NFT Marketplace Launch</h3>
            <p className="text-sm text-gray-400">
              Full-featured marketplace with Polygon minting, React front-end,
              and wallet integration
            </p>
          </div>

          {/* Project 2 */}
          <div 
            style={{ backgroundColor: LIGHT_CARD_BG }} 
            className="rounded-xl p-5 text-left border border-gray-700"
          >
            <div 
              style={{ backgroundColor: CARD_BG }} 
              className="h-40 rounded-lg mb-4 flex items-center justify-center"
            >
              <p className="text-gray-500">[Influencer NFT Drop screenshot]</p>
            </div>
            <h3 className="text-lg font-bold mb-2">Influencer NFT Drop</h3>
            <p className="text-sm text-gray-400">
              Themed collection with Ethereum deployment and custom mint page
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid md:grid-cols-3 gap-6 max-w-4xl w-full mb-16">
        <div style={{ backgroundColor: CARD_BG }} className="p-6 rounded-xl border border-gray-700">
          <p style={{ color: ACCENT_BLUE }} className="text-3xl font-bold mb-2">302%</p>
          <p className="text-sm text-gray-400">QoQ smart contract growth</p>
        </div>
        <div style={{ backgroundColor: CARD_BG }} className="p-6 rounded-xl border border-gray-700">
          <p style={{ color: ACCENT_BLUE }} className="text-3xl font-bold mb-2">$0.05</p>
          <p className="text-sm text-gray-400">Lowest minting cost (Solana)</p>
        </div>
        <div style={{ backgroundColor: CARD_BG }} className="p-6 rounded-xl border border-gray-700">
          <p style={{ color: ACCENT_BLUE }} className="text-3xl font-bold mb-2">7%</p>
          <p className="text-sm text-gray-400">Devs actively using NFT tools</p>
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-2xl w-full">
        <h2 className="text-2xl font-semibold mb-6"> Contact Us</h2>
        <p className="mb-6 text-gray-300">
          Launching August 2025 - Early adopters get special deals!
        </p>

        <div style={{ backgroundColor: CARD_BG }} className="rounded-xl p-6 mb-6 border border-gray-700">
          <p className="mb-4 text-gray-300">Email: lucid@mosaictechorg.com</p>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Your email"
              style={{ backgroundColor: LIGHT_CARD_BG }}
              className="w-full rounded-lg px-4 py-2 text-white placeholder-gray-500 border border-gray-700"
            />
            <textarea
              placeholder="Your message"
              rows="4"
              style={{ backgroundColor: LIGHT_CARD_BG }}
              className="w-full rounded-lg px-4 py-2 text-white placeholder-gray-500 border border-gray-700"
            ></textarea>
            <button 
              style={{ backgroundColor: ACCENT_BLUE, color: DARK_BG }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = ACCENT_BLUE_HOVER}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ACCENT_BLUE}
              className="font-bold py-2 px-6 rounded-lg shadow-lg transition duration-200"
            >
              Send Message
            </button>
          </div>
        </div>

        <div className="text-sm text-gray-400">
          <p className="mb-2">
            Twitter:{" "}
            <a
              href="https://twitter.com/MosaicGlobalMint"
              style={{ color: ACCENT_BLUE }}
              className="underline"
            >
              @MosaicGlobalMint
            </a>
          </p>
          <p>
            Parent brand:{" "}
            <a
              href="https://twitter.com/mosaicTech_"
              style={{ color: ACCENT_BLUE }}
              className="underline"
            >
              @mosaicTech_
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
